import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InvalidSetPasswordTokenError, setPassword as apiSetPassword } from '@apis/auth';
import styles from './styles.module.css';

/** Minimum password length displayed in the policy and enforced client-side.
 *  Mirrors the (minimal) server policy; the server is the source of truth. */
const MIN_PASSWORD_LENGTH = 8;

type Status = 'form' | 'invalidToken' | 'success';

/**
 * Public page reached from the invite / reset email link
 * (`/set-password?token=...`). Used for BOTH first-time invites and password
 * resets — same flow. Reads the token from the URL, collects a new password
 * (with confirm + the policy shown), and POSTs to /api/auth/set-password.
 */
export function SetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A missing token is terminal before the user types anything.
  const [status, setStatus] = useState<Status>(token ? 'form' : 'invalidToken');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErr(t('auth.setPassword.tooShort'));
      return;
    }
    if (password !== confirm) {
      setErr(t('auth.setPassword.mismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await apiSetPassword(token, password);
      setStatus('success');
    } catch (error) {
      if (error instanceof InvalidSetPasswordTokenError) {
        setStatus('invalidToken');
      } else {
        setErr(t('auth.setPassword.genericError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <main className={styles.card}>
        <div className={styles.accent} aria-hidden />
        <header className={styles.brand}>
          <img className={styles.logo} src="/orca.png" alt="" />
          <span className={styles.wordmark}>d-orca</span>
        </header>

        {status === 'success' ? (
          <>
            <div className={styles.heading}>
              <span className={styles.eyebrow}>{t('auth.setPassword.eyebrow')}</span>
              <h1 className={styles.title}>{t('auth.setPassword.successTitle')}</h1>
              <p className={styles.subtitle}>{t('auth.setPassword.successBody')}</p>
            </div>
            <button
              type="button"
              className={styles.submit}
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('auth.setPassword.goToLogin')}
            </button>
          </>
        ) : status === 'invalidToken' ? (
          <>
            <div className={styles.heading}>
              <span className={styles.eyebrow}>{t('auth.setPassword.eyebrow')}</span>
              <h1 className={styles.title}>{t('auth.setPassword.title')}</h1>
            </div>
            <div className={styles.error} role="alert">
              {token ? t('auth.setPassword.invalidToken') : t('auth.setPassword.missingToken')}
            </div>
            <div className={styles.forgotRow}>
              <Link className={styles.linkButton} to="/login">
                {t('auth.setPassword.goToLogin')}
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className={styles.heading}>
              <span className={styles.eyebrow}>{t('auth.setPassword.eyebrow')}</span>
              <h1 className={styles.title}>{t('auth.setPassword.title')}</h1>
              <p className={styles.subtitle}>{t('auth.setPassword.subtitle')}</p>
            </div>
            <form onSubmit={onSubmit} className={styles.form} noValidate>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('auth.setPassword.newPasswordLabel')}</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('auth.setPassword.confirmPasswordLabel')}</span>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              <div className={styles.policy}>
                <span className={styles.policyTitle}>{t('auth.setPassword.policyTitle')}</span>
                <ul className={styles.policyList}>
                  <li>{t('auth.setPassword.policyMinLength')}</li>
                </ul>
              </div>
              {err ? <div className={styles.error} role="alert">{err}</div> : null}
              <button type="submit" className={styles.submit} disabled={submitting}>
                {submitting ? t('auth.setPassword.submitting') : t('auth.setPassword.submit')}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
